import invariant from 'invariant'
import partial from 'lodash/partial'
import {exhaustive} from '@freckle/exhaustive'
import {fromMaybe, maybe, mmap, mthen} from '@freckle/maybe'
import {groupAllWith, headOnNonEmpty} from '@freckle/non-empty'
import type {AssignmentContextArg} from '@freckle/educator-entities/ts/common/helpers/assignment-context'
import {
  createAssignmentContextData,
  findAssignmentStartsAt,
} from '@freckle/educator-entities/ts/common/helpers/assignment-context'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import type {ProductT} from '@freckle/educator-entities/ts/common/helpers/products'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {fromOpenApiSession} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import type {ElaAssignmentType} from '@freckle/educator-entities/ts/ela/assignments/ela-assignment'
import {ElaAssignmentTypes} from '@freckle/educator-entities/ts/ela/assignments/ela-assignment'
import type {MathAssignmentTypeT} from '@freckle/educator-entities/ts/math/common/models/math-assignment'
import {MathAssignmentTypes} from '@freckle/educator-entities/ts/math/common/models/math-assignment'
import type {fancyOperations} from '@freckle/educator-entities/ts/openapi-clients'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import {getActiveCourses} from '@freckle/educator-entities/ts/roster/helpers/tagged-courses'
import {isIdpManaged} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import type {CourseMembershipAttrs} from '@freckle/educator-entities/ts/roster/models/course-membership'
import type {CourseToSubjectMapT} from '@freckle/educator-entities/ts/roster/models/course-subject'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import type {SelectedCourseToStudentIdsMapT} from '@freckle/classroom/ts/common/logic/student-course-picker'
import {forCourse} from '@freckle/classroom/ts/users/collections/students'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'

type AssignResponseItem = {assignmentId: number; studentIds: Array<number>}
export type MultiCourseAssignResponse = Array<AssignResponseItem & {courseId: number}>
export type MultiCourseAssignmentPostT = Map<
  number,
  {
    studentIds: Array<number>
    postData: object
    queryParams?: Partial<fancyOperations['postSubjectProductSessionsR']['parameters']['query']>
  }
>

export const mkMultiCourseAssignmentPost = (
  selectedStudents: SelectedCourseToStudentIdsMapT,
  postData: object,
  queryParams?: Partial<fancyOperations['postSubjectProductSessionsR']['parameters']['query']>,
): MultiCourseAssignmentPostT => {
  const entries: [number, number[]][] = Array.from(selectedStudents.entries())

  return new Map(
    entries.map(([courseId, studentIds]) => [courseId, {studentIds, postData, queryParams}]),
  )
}

export async function getMultiCourseAssignmentPromise(
  assignmentType: MathAssignmentTypeT | ElaAssignmentType,
  standardSetIds: StandardSetIds,
  postDataPerCourse: MultiCourseAssignmentPostT,
  markClaimedFreeResourcesStale: () => Promise<void>,
): Promise<MultiCourseAssignResponse> {
  const requestFn = getAssignmentReqFn(assignmentType, standardSetIds)

  const entries = Array.from(postDataPerCourse.entries())
  const perCourseResults = await Promise.all(
    entries.map(async ([courseId, {studentIds, postData, queryParams}]) => {
      const results = await requestFn(studentIds, postData, queryParams, {
        courseId,
        assignmentStartsAt: findAssignmentStartsAt(
          postData as {
            startsAt: number | undefined | null
          },
        ),
      })
      return results.map(r => ({...r, courseId}))
    }),
  )
  const responses = perCourseResults.flat()
  await markClaimedFreeResourcesStale()
  return responses
}

function getAssignmentReqFn(
  assignmentType: MathAssignmentTypeT | ElaAssignmentType,
  standardSetIds: StandardSetIds,
): (
  selectedStudentIds: Array<number>,
  postData: object,
  queryParams:
    Partial<fancyOperations['postSubjectProductSessionsR']['parameters']['query']> | undefined,
  context: AssignmentContextArg,
) => Promise<AssignResponseItem[]> {
  const elaAssignmentType = ElaAssignmentTypes.parse(assignmentType)
  const mathAssignmentType = MathAssignmentTypes.parse(assignmentType)
  if (elaAssignmentType !== null && elaAssignmentType !== undefined) {
    return partial(createElaAssignment, elaAssignmentType, standardSetIds)
  } else if (mathAssignmentType !== null && mathAssignmentType !== undefined) {
    return partial(createMathAssignment, mathAssignmentType, standardSetIds)
  } else {
    throw new Error(
      `Unable to create assignment due to unrecognized assignment type ${assignmentType}`,
    )
  }
}

function createMathAssignment(
  mathAssignmentType: MathAssignmentTypeT,
  standardSetIds: StandardSetIds,
  selectedStudentIds: Array<number>,
  postData: object,
  queryParams:
    Partial<fancyOperations['postSubjectProductSessionsR']['parameters']['query']> | undefined,
  context: AssignmentContextArg,
): Promise<AssignResponseItem[]> {
  switch (mathAssignmentType) {
    case 'MathAdaptive':
      return createAssignmentV3(
        Products.MathAdaptive,
        standardSetIds,
        selectedStudentIds,
        postData,
        queryParams,
        context,
      )
    case 'MathFocusSkillsPractice':
      return createAssignmentV3(
        Products.MathFocusSkillsPractice,
        standardSetIds,
        selectedStudentIds,
        postData,
        queryParams,
        context,
      )
    case 'MathStandard':
      return createAssignmentV3(
        Products.MathTargeted,
        standardSetIds,
        selectedStudentIds,
        postData,
        queryParams,
        context,
      )
    case 'MathAssessment':
    case 'MathIbl':
    case 'MathNumberFacts':
    case 'MathDepthOfKnowledgePractice':
      return createMathAssignmentV2(mathAssignmentType, selectedStudentIds, postData, context)

    case 'MathFactPractice':
      return createAssignmentV3(
        Products.MathFactPractice,
        standardSetIds,
        selectedStudentIds,
        postData,
        queryParams,
        context,
      )

    case 'MathTargetedDepthOfKnowledgePractice':
      return createAssignmentV3(
        Products.MathTargetedDepthOfKnowledgePractice,
        standardSetIds,
        selectedStudentIds,
        postData,
        queryParams,
        context,
      )

    default:
      return exhaustive(mathAssignmentType, 'MathAssignmentTypeT')
  }
}

function createElaAssignment(
  elaAssignmentType: ElaAssignmentType,
  standardSetIds: StandardSetIds,
  selectedStudentIds: Array<number>,
  postData: object,
  queryParams:
    Partial<fancyOperations['postSubjectProductSessionsR']['parameters']['query']> | undefined,
  context: AssignmentContextArg,
): Promise<AssignResponseItem[]> {
  switch (elaAssignmentType) {
    case 'ela_assessment':
    case 'ela_article':
    case 'ela_adaptive_skills':
    case 'ela_targeted_skills':
    case 'ela_decodable':
      return createElaAssignmentV2(elaAssignmentType, selectedStudentIds, postData, context)
    case 'ela_word_study':
      return createAssignmentV3(
        Products.ElaWordStudy,
        standardSetIds,
        selectedStudentIds,
        postData,
        queryParams,
        context,
      )
    case 'ela_focus_skills_practice':
      return createAssignmentV3(
        Products.ElaFocusSkillsPractice,
        standardSetIds,
        selectedStudentIds,
        {...postData, standardSetId: standardSetIds.ela},
        queryParams,
        context,
      )

    default:
      return exhaustive(elaAssignmentType, 'ElaAssignmentType')
  }
}

type PostMathAssignmentBody = NonNullable<
  fancyOperations['postMathAssignmentsR']['requestBody']
>['content']['application/json']

async function createMathAssignmentV2(
  type: MathAssignmentTypeT,
  students: Array<number>,
  metadata: PostMathAssignmentBody['metadata'],
  context: AssignmentContextArg,
): Promise<AssignResponseItem[]> {
  return fancyClient
    .POST('/2/math-assignments', {
      body: {
        type: type as PostMathAssignmentBody['type'],
        students,
        metadata,
        context: createAssignmentContextData(context),
      } as PostMathAssignmentBody,
    })
    .then(extractData)
    .then(data =>
      data.map(({id}) => ({
        assignmentId: id,
        studentIds: students,
      })),
    )
}

function createElaAssignmentV2(
  type: ElaAssignmentType,
  studentIds: Array<number>,
  data: object,
  context: AssignmentContextArg,
): Promise<AssignResponseItem[]> {
  let tag
  if (type === ElaAssignmentTypes.ElaAssessment) {
    tag = 'PostElaAssessment'
  } else if (type === ElaAssignmentTypes.ElaWordStudy) {
    tag = 'PostElaWordStudy'
  } else if (type === ElaAssignmentTypes.ElaDecodable) {
    tag = 'PostElaDecodables'
  } else {
    tag = 'PostArticle'
  }

  return fancyClient
    .POST('/2/ela-assignments', {
      body: {
        type,
        students: studentIds,
        metadata: {
          contents: data,
          tag,
        },
        context: createAssignmentContextData(context),
      },
    })
    .then(extractData)
    .then(data => {
      // It should be impossible for the classroom app to get the student
      // response type. Narrow on `id` rather than the response's declared
      // `type` discriminator, which isn't actually present on the wire.
      invariant('id' in data, 'Student return types are not supported')
      return data
    })
    .then(({id}) => [{assignmentId: id, studentIds}])
}

export type StandardSetIds = {
  math: string
  ela?: string | null
}

export const mkStandardSetIds = (
  teacher: Pick<TeacherAttrs, 'mathStandardSetId' | 'elaStandardSetId'>,
): StandardSetIds => ({
  math: teacher.mathStandardSetId,
  ela: teacher.elaStandardSetId,
})

export type PostAssignmentBody = NonNullable<
  fancyOperations['postSubjectProductSessionsR']['requestBody']
>['content']['application/json']

async function createAssignmentV3(
  product: ProductT,
  standardSetIds: StandardSetIds,
  studentIds: Array<number>,
  metadata: PostAssignmentBody['metadata'],
  queryParams:
    Partial<fancyOperations['postSubjectProductSessionsR']['parameters']['query']> | undefined,
  context: AssignmentContextArg,
): Promise<AssignResponseItem[]> {
  const paths = Products.getPaths(product)
  return fancyClient
    .POST('/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions', {
      params: {
        path: {
          'SubjectP-Subject': paths.subjectPath,
          'ProductP-AbbreviatedProduct': paths.productPath,
        },
        query: {
          'math-standard-set-id': standardSetIds.math,
          ...mmap(
            x =>
              ({'ela-standard-set-id': x}) satisfies Partial<
                fancyOperations['postSubjectProductSessionsR']['parameters']['query']
              >,
            standardSetIds.ela,
          ),
          ...queryParams,
        },
      },
      body: {
        studentIds,
        metadata,
        context: createAssignmentContextData(context),
        startsAt: maybe(
          () => null,
          startsAt => startsAt.valueOf(),
          context.assignmentStartsAt,
        ),
      },
    })
    .then(extractData)
    .then(data =>
      groupAllWith(({assignmentId}) => assignmentId, data.map(fromOpenApiSession)).map(
        genericSessions => ({
          assignmentId: headOnNonEmpty(genericSessions).assignmentId,
          studentIds: genericSessions.map(({studentId}) => studentId),
        }),
      ),
    )
}

export function getInitialCourseIdWithResources(
  courses: Array<Pick<CourseAttrs, 'id' | 'archived'>>,
  disabledCourseIds: Array<number>,
  initialCourseId: number,
): number | undefined {
  const {courses: activeCourses} = getActiveCourses(courses)
  const courseIds = activeCourses.map(c => c.id)
  const nonDisabledCourseIds = courseIds.filter(
    courseId => !disabledCourseIds.some(disabledId => disabledId === courseId),
  )

  const initialCourseIdDisabled = disabledCourseIds.some(
    disabledId => disabledId === initialCourseId,
  )

  // If the initial course id is disabled, get the first non-disabled course id
  return initialCourseIdDisabled ? nonDisabledCourseIds[0] : initialCourseId
}

type GetInitialStudentSelectionMapArgs = {
  courses: Array<Pick<CourseAttrs, 'id' | 'archived' | 'idpManaged'>>
  students: Array<Pick<StudentAttrs, 'id'>>
  courseMemberships: Array<CourseMembershipAttrs>
  initialCourseId: number
  initialStudentIds?: Array<number> | null | undefined
  currentSubject: SubjectT
  courseSubjectMap: CourseToSubjectMapT
  disabledCourseIds: Array<number>
}

export const getInitialStudentSelectionMap = ({
  courses,
  students,
  courseMemberships,
  initialCourseId,
  initialStudentIds,
  currentSubject,
  courseSubjectMap,
  disabledCourseIds,
}: GetInitialStudentSelectionMapArgs): SelectedCourseToStudentIdsMapT =>
  fromMaybe(
    () => new Map(),
    mthen(getInitialCourseIdWithResources(courses, disabledCourseIds, initialCourseId), courseId =>
      mthen(
        courses.find(c => c.id === courseId),
        course => {
          const courseStudents = forCourse(students, course.id, courseMemberships).map(x => x.id)
          const mayEmptyStudentIds = fromMaybe(() => courseStudents, initialStudentIds)
          const nonEmptyInitial =
            mayEmptyStudentIds.length === 0 ? courseStudents : mayEmptyStudentIds

          return mthen(courseSubjectMap.get(courseId), allowedSubjects =>
            allowedSubjects.includes(currentSubject) || isIdpManaged(course)
              ? new Map([[courseId, nonEmptyInitial]])
              : null,
          )
        },
      ),
    ),
  )
